import React, { Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from 'react'
import MaisConteudoAPi from '../services/MaisConteudoAPi'
import useUser from './UserContext';

export interface IOpenedWishList {
    action: 'create' | 'edit' | '';
    code?: string;
    linkName: string;
    products: {
        productRef: string;
    }[]
    vtexBrandName: string;
    linkId: string;
    name: string;
    description: string;
}

interface IWishListContext {
    wishListIsOpen: boolean;
    onOpenWishListHandler: () => void;
    onCloseWishListHandler: () => void;
    onAddProductHandler: (linkText: string, productName: string, productImage: string, productCode: string) => void;
    onDeleteProductHandler: (linkText: string) => void;
    openedWishList: IOpenedWishList;
    setOpenedWishList: Dispatch<SetStateAction<IOpenedWishList>>;
    onShareLinkHandler: (title?: string, description?: string, linkId?: string) => void;
    onShareLinkWithParamsHandler: (title?: string, description?: string, linkName?: string, linkId?: string, code?:string) => void;
    onShareLinkHandlerItem: (brandBaseUrl?: string) => void;
}

export const WishListContext = React.createContext({} as IWishListContext)


const WishListContextProvider: React.FC = ({ children }) => {
    const { userInfo } = useUser()

    const [wishListIsOpen, setWishListIsOpen] = useState<boolean>(false)
    const [openedWishList, setOpenedWishList] = useState<IOpenedWishList>({
        action: '',
        name: '',
        code: '',
        linkName: '',
        description: '',
        linkId: '',
        vtexBrandName: '',
        products: []
    })

    const { isLogged } = useUser()

    const onCleanProductsWishListHandler = useCallback(() => (
        setOpenedWishList({ action: '', name: '',code: '', linkName: '', description: '', linkId: '', vtexBrandName: '', products: [] })
    ), [])

    const onOpenWishListHandler = useCallback(() => setWishListIsOpen(true), [])

    const onCloseWishListHandler = useCallback(() => {
        setWishListIsOpen(false)
        onCleanProductsWishListHandler()
    }, [onCleanProductsWishListHandler])

    const onAddProductHandler = useCallback(async (productRef, productName, productImage, productCode) => {
        try {
            await MaisConteudoAPi.post(`/v1/wishlists/${openedWishList.linkId}/add`, {
                products: [{ productRef, productName, productImage, productCode }]
            })
            setOpenedWishList((prevState) => ({ ...prevState, products: prevState.products.concat({ productRef }), code: userInfo.code }))
        } catch (error) {
            console.log(error)
        }
    }, [openedWishList.linkId, userInfo.code])

    const onDeleteProductHandler = useCallback(async (productRef) => {
        try {
            await MaisConteudoAPi.post(`/v1/wishlists/${openedWishList.linkId}/remove`, { products: [productRef] })
            setOpenedWishList((prevState) => ({ ...prevState, products: prevState.products.filter((product) => product.productRef !== productRef) }))
        } catch (error) {
            console.log(error)
        }
    }, [openedWishList.linkId])

    const onShareLinkHandler = useCallback((title?: string, linkId?: string) => {
        navigator
            .share({
                title: title || openedWishList.name,
                url: `/lojinha/${linkId || openedWishList.linkId}`,
            })
    }, [openedWishList])

    const onShareLinkWithParamsHandler = useCallback((title?: string, linkName?: string, code?:string, linkId?: string) => {
        navigator
            .share({
                title: title || openedWishList.name,
                url: `/${linkName || openedWishList.linkName }/${code || openedWishList.code}/${linkId || openedWishList.linkId}`,
            })
    }, [openedWishList.code, openedWishList.linkId, openedWishList.linkName, openedWishList.name])

    const onShareLinkHandlerItem = useCallback((brandBaseUrl?: string) => {
        navigator
            .share({
                url: (`${brandBaseUrl}`)
            })
    }, [])

    const onCheckLogoutToCloseWishlist = useCallback(() => {
        if (!isLogged) {
            return onCloseWishListHandler()
        }
    }, [isLogged, onCloseWishListHandler])

    useEffect(() => {
        onCheckLogoutToCloseWishlist()
    }, [onCheckLogoutToCloseWishlist])

    return (
        <WishListContext.Provider value={{
            wishListIsOpen,
            onOpenWishListHandler,
            openedWishList,
            onCloseWishListHandler,
            onAddProductHandler,
            onDeleteProductHandler,
            setOpenedWishList,
            onShareLinkHandler,
            onShareLinkWithParamsHandler,
            onShareLinkHandlerItem
        }}>
            {children}
        </WishListContext.Provider>
    )
}



const useWishList = () => {
    const {
        wishListIsOpen,
        onOpenWishListHandler,
        openedWishList,
        onCloseWishListHandler,
        onAddProductHandler,
        onDeleteProductHandler,
        setOpenedWishList,
        onShareLinkHandler,
        onShareLinkWithParamsHandler,
        onShareLinkHandlerItem
    } = useContext(WishListContext)

    return {
        wishListIsOpen,
        onOpenWishListHandler,
        openedWishList,
        onCloseWishListHandler,
        onAddProductHandler,
        onDeleteProductHandler,
        setOpenedWishList,
        onShareLinkHandler,
        onShareLinkWithParamsHandler,
        onShareLinkHandlerItem
    }
}

export default useWishList

export { WishListContextProvider }
